import { FullscreenTray } from '../../../domain/fullscreen-tray';
import {
  CreateFullscreenPageItemRequestApiModel,
  FullscreenPageItemApiModel,
  SubTypeApiModel,
  UpdateFullscreenPageItemRequestApiModel,
} from '@cxb/editor-stb-api';
import { mapperPipe, remainderMapper } from 'osn-shared';
import {
  nameLocalizedMapperDomainToApi,
  scheduledDateMapperDomainToApi,
  targetingMapperDomainToApi,
} from '../../mapping/create-tray-domain-to-api';
import { FullscreenItemApiModel } from '@cxb/editor-stb-api/model/models';
import {
  commonMapper,
  nameLocalizedMapperApiToDomain,
  scheduledDateMapperApiToDomain,
  targetingMapperApiToDomain,
} from '../../mapping/create-tray-api-to-domain';
import { FullscreenTrayFormViewModel } from './fullscreen-tray-form.view-model';
import { TrayFormViewModelType } from '../view-model-common/view-model-type';
import {
  commonMapperDomainToViewModel,
  nameLocalizedMapperDomainToViewModel,
  scheduledDateMapperDomainToViewModel,
  targetingMapper,
} from '../create-tray-screen/mapping/create-tray-domain-to-vm';
import {
  commonMapperViewModelToDomain,
  nameLocalizedMapperViewModelToDomain,
  scheduledDateMapperViewModelToDomain,
} from '../create-tray-screen/mapping/create-tray-vm-to-domain';

export const mapFullscreenTrayDomainModelToApiModel = (
  item: FullscreenTray,
  pageVersionId?: string,
  tenantId?: string
): CreateFullscreenPageItemRequestApiModel & UpdateFullscreenPageItemRequestApiModel => {
  const commonProperties = mapperPipe(item, nameLocalizedMapperDomainToApi, targetingMapperDomainToApi, scheduledDateMapperDomainToApi);

  const { subtype, items } = item;

  return remainderMapper(commonProperties, () => ({
    pageVersionId,
    subType: subtype,
    items: items.map((fullscreenItem, index: number) => {
      const {
        id,
        headlineEnglish,
        headlineArabic,
        descriptionEnglish,
        descriptionArabic,
        buttonTextEnglish,
        buttonTextArabic,
        backgroundImage,
        channel,
        programStartTime,
        programEndTime,
        programId,
        instanceId,
        feed,
        program,
        contentGroup,
      } = fullscreenItem;

      return {
        id,
        order: index + 1,
        headlineLocalized: {
          en: headlineEnglish,
          ar: headlineArabic,
        },
        descriptionLocalized: {
          en: descriptionEnglish,
          ar: descriptionArabic,
        },
        ctaLabelLocalized: {
          en: buttonTextEnglish,
          ar: buttonTextArabic,
        },
        backgroundImage: backgroundImage.id,
        ...(subtype === SubTypeApiModel.LiveTvProgram && {
          channelId: channel?.id,
          programmingTime: programStartTime ? new Date(programStartTime).getTime() : undefined,
          programmingEndTime: programEndTime ? new Date(programEndTime).getTime() : undefined,
          programId,
          instanceId,
        }),
        ...(subtype === SubTypeApiModel.OsnPlusContentGroup && {
          feedId: feed?.id?.toString(),
        }),
        ...(subtype === SubTypeApiModel.OsnPlusContent && {
          contentId: program?.id?.toString(),
        }),
        ...((subtype === SubTypeApiModel.OndemandContentGroup || subtype === SubTypeApiModel.StoreContentGroup) && {
          contentGroupId: contentGroup?.id?.toString(),
        }),
      };
    }),
    tenantId,
  }));
};

const getSubType = (pageItem: FullscreenPageItemApiModel): SubTypeApiModel => {
  if (pageItem.items && pageItem.items.length > 0) {
    const firstItem = pageItem.items[0];

    if (firstItem.feedId) {
      return SubTypeApiModel.OsnPlusContentGroup;
    } else if (firstItem.contentId) {
      return SubTypeApiModel.OsnPlusContent;
    } else if (firstItem.contentGroup) {
      return pageItem.subType ?? SubTypeApiModel.OndemandContentGroup;
    } else {
      return SubTypeApiModel.LiveTvProgram;
    }
  }
  return SubTypeApiModel.OsnPlusContent;
};

export const mapFullscreenTrayApiModelToDomainModel = (pageItem: FullscreenPageItemApiModel): FullscreenTray => {
  const subType = getSubType(pageItem);

  return remainderMapper(
    mapperPipe(pageItem, commonMapper, nameLocalizedMapperApiToDomain, targetingMapperApiToDomain, scheduledDateMapperApiToDomain),
    () => ({
      type: 'FullscreenTray',
      subtype: subType,
      items: pageItem.items.map((item: FullscreenItemApiModel) => {
        const {
          id,
          headlineLocalized,
          descriptionLocalized,
          ctaLabelLocalized,
          programmingTime,
          programmingEndTime,
          backgroundImage,
          channel,
          programId,
          instanceId,
          contentGroup,
        } = item;

        return {
          id,
          backgroundImage: {
            id: backgroundImage.id,
            name: backgroundImage.originalName,
            publicUrl: backgroundImage.publicUrl,
          },
          headlineEnglish: headlineLocalized.en,
          headlineArabic: headlineLocalized.ar,
          descriptionEnglish: descriptionLocalized.en,
          descriptionArabic: descriptionLocalized.ar,
          buttonTextEnglish: ctaLabelLocalized?.en,
          buttonTextArabic: ctaLabelLocalized?.ar,
          channel: channel
            ? {
                id: channel.id,
                channelID: channel.channelId,
                channelName: channel.channelName,
                channelNumber: channel.channelNumber,
              }
            : undefined,

          programStartTime: new Date(programmingTime).toISOString(),
          programEndTime: new Date(programmingEndTime).toISOString(),
          programId,
          instanceId,
          feed: item['feed'],
          program: item['program'],
          ...((subType === SubTypeApiModel.OndemandContentGroup || subType === SubTypeApiModel.StoreContentGroup) && {
            contentGroup: {
              id: contentGroup.id,
              name: contentGroup.title,
              modified: contentGroup.updatedAt,
              referringPages: contentGroup.referringPageNames || [],
            },
          }),
        };
      }),
    })
  );
};

export const mapFullscreenTrayDomainModelToViewModel = (fullscreenPageItem: FullscreenTray): FullscreenTrayFormViewModel => {
  const commonProperties = mapperPipe(
    fullscreenPageItem,
    commonMapperDomainToViewModel,
    nameLocalizedMapperDomainToViewModel,
    targetingMapper,
    scheduledDateMapperDomainToViewModel
  );

  const { subtype, items } = fullscreenPageItem;

  return remainderMapper(commonProperties, () => ({
    type: TrayFormViewModelType.FullscreenTray,
    subType: subtype,
    items: items.map(
      ({
        id,
        backgroundImage,
        headlineArabic,
        headlineEnglish,
        descriptionArabic,
        descriptionEnglish,
        buttonTextArabic,
        buttonTextEnglish,
        channel,
        programId,
        programEndTime,
        programStartTime,
        instanceId,
        feed,
        program,
        contentGroup,
      }) => {
        return {
          id,
          backgroundImage,
          headlineEnglish,
          headlineArabic,
          descriptionEnglish,
          descriptionArabic,
          buttonTextEnglish,
          buttonTextArabic,
          programSelector: {
            channel,
            programId,
            programEndTime: new Date(programEndTime).getTime(),
            programStartTime: new Date(programStartTime).getTime(),
            instanceId,
          },
          feed,
          program,
          contentGroup,
        };
      }
    ),
  }));
};

export const mapFullscreenTrayViewModelToDomainModel = (vm: FullscreenTrayFormViewModel): FullscreenTray => {
  const commonProperties = mapperPipe(
    vm,
    commonMapperViewModelToDomain,
    nameLocalizedMapperViewModelToDomain,
    targetingMapper,
    scheduledDateMapperViewModelToDomain
  );
  const { subType, items } = vm;

  return remainderMapper(commonProperties, () => ({
    type: 'FullscreenTray',
    subtype: subType,
    items: items.map((item) => {
      const { programSelector, program, feed, contentGroup } = item;

      switch (subType) {
        case SubTypeApiModel.LiveTvProgram:
          return {
            ...item,
            channel: programSelector.channel,
            programId: programSelector.programId,
            instanceId: programSelector.instanceId,
            programStartTime: new Date(programSelector.programStartTime).toISOString(),
            programEndTime: new Date(programSelector.programEndTime).toISOString(),
          };
        case SubTypeApiModel.OsnPlusContent:
          return {
            ...item,
            programId: `${program.id}`,
            programStartTime: new Date(program.availabilityFrom).toISOString(),
            programEndTime: new Date(program.availabilityTo).toISOString(),
          };
        case SubTypeApiModel.OsnPlusContentGroup:
          return {
            ...item,
            programId: `${feed.id}`,
          };
        case SubTypeApiModel.OndemandContentGroup:
        case SubTypeApiModel.StoreContentGroup:
          return {
            ...item,
            contentGroup,
          };
      }
    }),
  }));
};
